/**
 * TanStack Query mutation hook for `POST /api/cro/admin/targets`.
 *
 * Per PATTERNS.md "useCroTargetMutation.ts (NEW)" — no direct analog in the
 * SPA (first useMutation in the codebase). Uses pessimistic save per
 * RESEARCH.md §6.2.3: await server response, THEN update UI state.
 *
 * onSuccess invalidations:
 *   - ['cro', 'targets']  — refreshes the admin table (history sparkline + current value)
 *   - ['cro', 'scorecard'] — legacy/unused key; kept for symmetry
 *   - ['cro', 'metric']   — refreshes the 9 big-number tiles on CroScorecardPage
 *   - ['cro', 'trend']    — refreshes the 2 trend tiles (CRO-06/07)
 *
 * All invalidations are fire-and-forget (void) so the admin page doesn't block
 * on scorecard cache updates. The user can navigate back to / when ready.
 *
 * Trust boundary: caller must be in the sg-saledash-cro Entra group — enforced
 * at the Fastify POST /api/cro/admin/targets route handler (assertCroGroupMember).
 * AdminTargetsPage also checks useCroMe().isCroGroupMember for defense-in-depth.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch, type ApiClientError, type ApiServerError } from '../lib/api-client.js';

import type { TargetEditBody, TargetEditResponse } from '@saledash/shared';

export type MutationError = ApiServerError | ApiClientError;

export function useCroTargetMutation() {
  const qc = useQueryClient();

  return useMutation<TargetEditResponse, MutationError, TargetEditBody>({
    mutationFn: (body) =>
      apiFetch<TargetEditResponse>('/api/cro/admin/targets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // Invalidate in order of visibility priority: admin targets first (the
      // history sparkline and current value need to update immediately), then
      // the scorecard tiles (async background refresh).
      void qc.invalidateQueries({ queryKey: ['cro', 'targets'] });
      void qc.invalidateQueries({ queryKey: ['cro', 'scorecard'] });
      void qc.invalidateQueries({ queryKey: ['cro', 'metric'] });
      void qc.invalidateQueries({ queryKey: ['cro', 'trend'] });
    },
  });
}
